import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Eye, Shield, Building2, User, Archive, MapPin } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import UserPurgeModal from '../../components/admin/UserPurgeModal';
import { adminApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';

import { formatDateOnlyEST } from '../../utils/dateUtils';

// V5 User Type Badge Component
const UserTypeBadge = ({ role, accountType, email, isAdmin: isAdminProp }) => {
  // Check multiple conditions for admin status
  const isAdmin = isAdminProp || accountType === 'admin' || role === 'admin' || role === 'master_admin';
  const isAdvertiser = accountType === 'advertiser' || role === 'advertiser';
  const isVenuePartner = role === 'venue_partner';
  
  if (isAdmin) {
    return (
      <span className="px-2 py-0.5 bg-admin/10 text-admin text-xs font-medium rounded-full flex items-center gap-1 w-fit">
        <Shield size={12} />
        Admin
      </span>
    );
  }
  
  if (isVenuePartner) {
    return (
      <span className="px-2 py-0.5 bg-venue/10 text-venue text-xs font-medium rounded-full flex items-center gap-1 w-fit">
        <MapPin size={12} />
        Venue Partner
      </span>
    );
  }
  
  if (isAdvertiser) {
    return (
      <span className="px-2 py-0.5 bg-business/10 text-business text-xs font-medium rounded-full flex items-center gap-1 w-fit">
        <Building2 size={12} />
        Advertiser
      </span>
    );
  }
  
  return (
    <span className="px-2 py-0.5 bg-consumer/10 text-consumer text-xs font-medium rounded-full flex items-center gap-1 w-fit">
      <User size={12} />
      Consumer
    </span>
  );
};

const AdminCustomers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    consumers: 0,
    advertisers: 0,
    venuePartners: 0,
    admins: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [purgeUser, setPurgeUser] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, [filterType]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const params = { search: searchQuery, limit: 100 };
      if (filterType) {
        params.account_type = filterType;
      }
      const response = await adminApi.get(`/admin/customers`, { params });
      setCustomers(response.data.customers);
      if (response.data.counts) {
        const apiCounts = response.data.counts;
        setCounts({
          total: apiCounts.total || 0,
          consumers: apiCounts.consumers || 0,
          advertisers: apiCounts.advertisers || 0,
          venuePartners: apiCounts.venue_partners || apiCounts.venuePartners || 0,
          admins: apiCounts.admins || 0
        });
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCustomers();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateOnlyEST(dateString);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-consumer border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-consumer text-sm mb-1">// admin.users</p>
          <h1 className="font-display text-3xl text-navy">Users</h1>
          <p className="text-navy/50 mt-1">{counts.total} registered users</p>
        </div>
        
        <button
          onClick={() => navigate('/admin/customers/archived')}
          className="px-4 py-2.5 border border-navy/20 rounded-xl text-navy/70 hover:bg-white hover:border-navy/30 transition-all flex items-center gap-2"
        >
          <Archive className="w-4 h-4" />
          View Archived
        </button>
      </div>

      {/* User Type Filter Pills with V5 Color-Coded Active States */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={() => setFilterType('')}
          className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
            filterType === '' ? 'bg-white text-navy shadow-sm' : 'text-navy/60 hover:bg-white/50'
          }`}
        >
          All ({counts.total})
        </button>
        <button
          onClick={() => setFilterType('consumer')}
          className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
            filterType === 'consumer' ? 'text-white' : 'text-navy/60 hover:bg-white/50'
          }`}
          style={filterType === 'consumer' ? {
            backgroundColor: '#f97316',
            boxShadow: '0 4px 15px rgba(249, 115, 22, 0.5), 0 0 20px rgba(249, 115, 22, 0.3)'
          } : {}}
        >
          <User size={16} />
          Consumer ({counts.consumers})
        </button>
        <button
          onClick={() => setFilterType('advertiser')}
          className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
            filterType === 'advertiser' ? 'text-white' : 'text-navy/60 hover:bg-white/50'
          }`}
          style={filterType === 'advertiser' ? {
            backgroundColor: '#8b5cf6',
            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.5), 0 0 20px rgba(139, 92, 246, 0.3)'
          } : {}}
        >
          <Building2 size={16} />
          Advertiser ({counts.advertisers})
        </button>
        <button
          onClick={() => setFilterType('venue_partner')}
          className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
            filterType === 'venue_partner' ? 'text-white' : 'text-navy/60 hover:bg-white/50'
          }`}
          style={filterType === 'venue_partner' ? {
            backgroundColor: '#14b8a6',
            boxShadow: '0 4px 15px rgba(20, 184, 166, 0.5), 0 0 20px rgba(20, 184, 166, 0.3)'
          } : {}}
        >
          <MapPin size={16} />
          Venue Partner ({counts.venuePartners})
        </button>
        <button
          onClick={() => setFilterType('admin')}
          className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
            filterType === 'admin' ? 'text-white' : 'text-navy/60 hover:bg-white/50'
          }`}
          style={filterType === 'admin' ? {
            backgroundColor: '#6366f1',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3)'
          } : {}}
        >
          <Shield size={16} />
          Admin ({counts.admins})
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy/5 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-navy/30 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name, email, or username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-12 pr-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-consumer transition-colors"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-orange-500/20 transition-all"
          >
            Search
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
        {/* Table Header */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-navy/5 bg-cream/50">
          <div className="col-span-4 text-xs font-semibold text-navy/50 uppercase tracking-wider">User</div>
          <div className="col-span-3 text-xs font-semibold text-navy/50 uppercase tracking-wider">Email</div>
          <div className="col-span-2 text-xs font-semibold text-navy/50 uppercase tracking-wider">Stats</div>
          <div className="col-span-2 text-xs font-semibold text-navy/50 uppercase tracking-wider">Last Active</div>
          <div className="col-span-1 text-xs font-semibold text-navy/50 uppercase tracking-wider">Actions</div>
        </div>

        {/* Table Body */}
        {customers.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No users found</h3>
            <p className="text-navy/50">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="divide-y divide-navy/5">
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="user-row px-4 md:px-6 py-4 cursor-pointer transition-colors hover:bg-cream/30"
                onClick={() => navigate(`/admin/customers/${customer.id}`)}
              >
                {/* Mobile Layout */}
                <div className="md:hidden">
                  <div className="flex items-start justify-between gap-3">
                    {/* User Avatar & Name */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {customer.picture ? (
                        <img 
                          src={resolveAssetUrl(customer.picture)}
                          alt={customer.first_name || 'User'}
                          className="w-10 h-10 rounded-full object-cover border-2 border-navy/10 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-navy to-navy/70 flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {(customer.first_name?.[0] || customer.email?.[0] || '?').toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-navy truncate">
                          {customer.first_name || customer.last_name
                            ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                            : 'Unnamed User'}
                        </p>
                        <p className="text-navy/60 text-sm truncate">{customer.email}</p>
                      </div>
                    </div>
                    {/* Action Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${customer.id}`); }}
                      className="p-2 bg-consumer/10 text-consumer rounded-lg hover:bg-consumer/20 transition-colors flex-shrink-0"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Mobile Stats Row */}
                  <div className="flex items-center gap-4 mt-3 pl-[52px]">
                    <UserTypeBadge
                      role={customer.role}
                      accountType={customer.account_type}
                      email={customer.email}
                    />
                    <span className="text-xs text-navy/50">
                      {customer.total_claims || 0} claimed · {customer.total_redeemed || 0} redeemed
                    </span>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                  {/* User Info */}
                  <div className="col-span-4 flex items-center gap-3">
                    {customer.picture ? (
                      <img 
                        src={resolveAssetUrl(customer.picture)}
                        alt={customer.first_name || 'User'}
                        className="w-10 h-10 rounded-full object-cover border-2 border-navy/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-navy to-navy/70 flex items-center justify-center text-white font-semibold">
                        {(customer.first_name?.[0] || customer.email?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-navy">
                        {customer.first_name || customer.last_name
                          ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                          : 'Unnamed User'}
                      </p>
                      <UserTypeBadge
                        role={customer.role}
                        accountType={customer.account_type}
                        email={customer.email}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-span-3">
                    <p className="text-navy/70 text-sm truncate">{customer.email}</p>
                  </div>

                  {/* Stats */}
                  <div className="col-span-2 flex items-center gap-4">
                    <div>
                      <p className="font-semibold text-navy">{customer.total_claims || 0}</p>
                      <p className="text-xs text-navy/50">Claimed</p>
                    </div>
                    <div>
                      <p className="font-semibold text-navy">{customer.total_redeemed || 0}</p>
                      <p className="text-xs text-navy/50">Redeemed</p>
                    </div>
                  </div>

                  {/* Last Active */}
                  <div className="col-span-2">
                    <p className="text-sm text-navy/70">{formatDate(customer.last_active || customer.updated_at)}</p>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/admin/customers/${customer.id}`)}
                      className="p-2 bg-consumer/10 text-consumer rounded-lg hover:bg-consumer/20 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination placeholder */}
        {customers.length > 0 && (
          <div className="px-6 py-4 border-t border-navy/5 flex items-center justify-between">
            <p className="text-navy/50 text-sm">Showing {customers.length} users</p>
          </div>
        )}
      </div>

      {/* Purge Modal */}
      {purgeUser && (
        <UserPurgeModal
          isOpen={!!purgeUser}
          onClose={() => setPurgeUser(null)}
          user={purgeUser}
          onPurgeComplete={fetchCustomers}
        />
      )}
    </AdminLayout>
  );
};

export default AdminCustomers;
